/**
 * FlowNodeDetails — the system selected in a flow diagram: the status it ended
 * on, time spent, then each status it reported with the gap between them.
 * Drawn on the same dark surface as the diagram nodes, so this is plain markup
 * rather than a Cloudscape Table, which is light-themed.
 */
import { FlowNode, FlowTone } from 'src/store/slices/paymentDetail/paymentDetailTypes';

const GLYPHS: Record<FlowTone, string> = { success: '✓', failed: '✕', neutral: '•' };

interface FlowNodeDetailsProps {
  node: FlowNode;
  onClose: () => void;
}

export const FlowNodeDetails = ({ node, onClose }: FlowNodeDetailsProps) => {
  // The API does not carry step timestamps yet; show the column only when it does.
  const showTimes = node.steps.some(step => !!step.at);

  return (
    <div className="flow-panel">
      <header className="flow-panel__header">
        <span className="flow-panel__system">{node.system}</span>
        <span className="flow-panel__count">({node.steps.length})</span>
        <button
          type="button"
          className="flow-panel__close"
          onClick={onClose}
          aria-label={`Close ${node.system} detail`}
        >
          ✕
        </button>
      </header>

      <div className="flow-panel__meta">
        {node.lastStatus && (
          <span
            className={`flow-panel__state flow-panel__tone--${node.tone}`}
            title={[node.lastStatus, node.state].filter(Boolean).join(' · ')}
          >
            <span aria-hidden="true">{GLYPHS[node.tone]}</span> {node.lastStatus}
          </span>
        )}
        {node.latency && <span className="flow-panel__latency">{node.latency} in system</span>}
        {node.bottleneck && <span className="flow-chip flow-chip--warning">Slowest</span>}
      </div>

      {node.passThrough ? (
        <p className="flow-panel__empty">No reported phases — the payment passed straight through.</p>
      ) : (
        <table className="flow-panel__table">
          <thead>
            <tr>
              <th scope="col">Status</th>
              {showTimes && <th scope="col">At</th>}
              <th scope="col">Gap from previous</th>
            </tr>
          </thead>
          <tbody>
            {node.steps.map(step => (
              <tr key={step.id}>
                <td>
                  <span className={`flow-panel__tone--${step.tone}`} title={step.state}>
                    <span aria-hidden="true">{GLYPHS[step.tone]}</span> {step.code}
                  </span>
                  {step.countLabel && <span className="flow-panel__badge">{step.countLabel}</span>}
                </td>
                {showTimes && <td className="flow-panel__gap">{step.at ?? '—'}</td>}
                <td className="flow-panel__gap">{step.gapFromPrevious ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
